import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../store/authStore';
import { isSuperAdmin } from '../../../lib/roles';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import StatTile from '../../../components/ui/StatTile';
import FilterChip from '../../../components/ui/FilterChip';
import {
  useOrganizationsList,
  useCreateOrganization,
  useSetOrganizationStatus,
} from '../hooks/useOrganizations';
import OrganizationsList from './OrganizationsList';
import CreateOrganizationModal from './CreateOrganizationModal';

/**
 * Platform super-admin organisations screen. Gated like TeamSettings:
 * redirect non-super-admins; queries enabled only when permitted.
 */
export default function SuperAdminOrganizations() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const allowed = isSuperAdmin(user);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (user !== null && !isSuperAdmin(user)) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const { data, isLoading, error } = useOrganizationsList({
    page,
    search,
    status,
    enabled: allowed,
  });

  const createMutation = useCreateOrganization({
    onSuccess: () => setShowCreateModal(false),
  });

  const statusMutation = useSetOrganizationStatus();

  const organizations = data?.organizations ?? [];
  const pagination = data?.pagination;

  // Counts come from the server so they describe every organisation, not just this page,
  // and stay stable while a status filter narrows the list below.
  const kpis = {
    total: data?.counts?.total ?? 0,
    active: data?.counts?.active ?? 0,
    suspended: data?.counts?.inactive ?? 0,
  };

  const handleStatusFilter = (next) => {
    setStatus(next);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleSetStatus = (org, active) => {
    const action = active ? 'reactivate' : 'suspend';
    if (!window.confirm(`Are you sure you want to ${action} ${org.name}?`)) return;
    statusMutation.mutate({ id: org.id, active });
  };

  const handleCreate = (payload) => {
    createMutation.mutate(payload);
  };

  if (user !== null && !allowed) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="page team-page">
        <p className="text-muted" style={{ padding: '2rem', textAlign: 'center' }}>
          Loading organisations…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page team-page">
        <header className="settings-tab-header settings-tab-header--split">
          <h1 className="settings-tab-title">Organisations</h1>
        </header>
        <p className="text-muted" style={{ padding: '2rem', textAlign: 'center' }}>
          Failed to load organisations.
        </p>
      </div>
    );
  }

  return (
    <div className="page team-page">
      <header className="settings-tab-header settings-tab-header--split">
        <h1 className="settings-tab-title">Organisations</h1>
        <div className="settings-tab-header-actions">
          <Button
            type="button"
            variant="primary"
            className="team-btn-invite"
            onClick={() => setShowCreateModal(true)}
          >
            Create organisation
          </Button>
        </div>
      </header>

      <div className="settings-stat-row">
        <Card><StatTile value={kpis.total} label="Total organisations" /></Card>
        <Card><StatTile value={kpis.active} label="Active" tone="success" /></Card>
        <Card><StatTile value={kpis.suspended} label="Suspended" tone="danger" /></Card>
      </div>

      <div className="filters">
        <Input
          id="org-search"
          placeholder="Search name or code…"
          value={search}
          onChange={handleSearchChange}
          aria-label="Search organisations"
        />
        <FilterChip active={status === ''} onClick={() => handleStatusFilter('')}>
          All
        </FilterChip>
        <FilterChip active={status === 'active'} onClick={() => handleStatusFilter('active')}>
          Active
        </FilterChip>
        <FilterChip active={status === 'inactive'} onClick={() => handleStatusFilter('inactive')}>
          Suspended
        </FilterChip>
      </div>

      <OrganizationsList
        organizations={organizations}
        currentOrganizationId={user?.organizationId}
        onSetStatus={handleSetStatus}
        isUpdating={statusMutation.isLoading}
      />

      {pagination && pagination.totalPages > 1 && (
        <div className="form-actions" style={{ justifyContent: 'center', marginTop: '1rem' }}>
          <Button
            type="button"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className="text-muted" style={{ alignSelf: 'center' }}>
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <Button
            type="button"
            variant="secondary"
            disabled={!pagination.hasNextPage}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <CreateOrganizationModal
        isOpen={showCreateModal}
        onClose={() => !createMutation.isLoading && setShowCreateModal(false)}
        onSubmit={handleCreate}
        isSubmitting={createMutation.isLoading}
      />
    </div>
  );
}
